import { useEffect, useRef, useState } from "react";
import "./ccBook.css";

import calendlyMark from "../../../assets/Calendly Logo.webp";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xdkzvppw";
const CALENDLY_BASE =
  "https://calendly.com/elliotcarlson-rain/rain-meeting-30-mins";

const EMPTY = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  school: "",
  role: "",
  notes: "",
};

const SIZES = [
  "Under 1,000 students",
  "1,000 – 5,000 students",
  "5,000 – 15,000 students",
  "15,000+ students",
];

/* Calendly's assets are only pulled in once someone has actually asked
   for a time, so the page doesn't pay for them on load. */
const loadCalendly = () =>
  new Promise((resolve) => {
    if (window.Calendly) return resolve();

    if (!document.querySelector("link[data-calendly]")) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      link.dataset.calendly = "true";
      document.head.appendChild(link);
    }

    if (!document.querySelector("script[data-calendly]")) {
      const script = document.createElement("script");
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      script.dataset.calendly = "true";
      document.head.appendChild(script);
    }

    const poll = () => (window.Calendly ? resolve() : setTimeout(poll, 100));
    poll();
  });

const CcBook = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [calendlyUrl, setCalendlyUrl] = useState(null);
  const calendlyRef = useRef(null);

  const change = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: `${form.firstName} ${form.lastName}`.trim(),
          email: form.email,
          phone: form.phone,
          school: form.school,
          role: form.role,
          notes: form.notes,
          source: "Career center page: book a walkthrough",
        }),
      });
    } catch (err) {
      // Best effort: never block the booking on the lead capture.
      console.error("Lead submission failed:", err);
    }

    const enc = encodeURIComponent;
    setCalendlyUrl(
      `${CALENDLY_BASE}?hide_event_type_details=1&hide_gdpr_banner=1` +
        `&name=${enc(`${form.firstName} ${form.lastName}`.trim())}` +
        `&email=${enc(form.email)}` +
        `&a1=${enc(form.school)}`
    );
    setSubmitting(false);
  };

  useEffect(() => {
    if (!calendlyUrl) return undefined;
    let cancelled = false;

    loadCalendly().then(() => {
      if (cancelled || !calendlyRef.current) return;
      window.Calendly.initInlineWidget({
        url: calendlyUrl,
        parentElement: calendlyRef.current,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [calendlyUrl]);

  return (
    <section className="cc-book" id="book">
      <div className="home-container cc-book-grid">
        <div className="cc-book-copy">
          <h2 className="cc-book-title">
            See what RAIN looks like on your campus
          </h2>
          <p className="cc-book-sub">
            Share a little about your office and we&rsquo;ll show you RAIN set up
            for a campus like yours.
          </p>
        </div>

        <div className="cc-book-form-wrap">
          {calendlyUrl ? (
            <div className="cc-book-booking">
              <button
                type="button"
                className="cc-book-back"
                onClick={() => setCalendlyUrl(null)}
              >
                &larr; Back to the form
              </button>
              <p className="cc-book-step2">Choose a time to meet</p>
              <div ref={calendlyRef} className="cc-book-calendly" />
            </div>
          ) : (
            <form className="cc-book-form" onSubmit={submit}>
              <p className="cc-book-powered">
                <img width={320} height={180} decoding="async" loading="lazy" src={calendlyMark} alt="" />
                Powered by Calendly. You&rsquo;ll choose a time on the
                next step
              </p>

              <div className="cc-book-row">
                <label className="cc-field">
                  <span>First name <i>*</i></span>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="Jordan"
                    value={form.firstName}
                    onChange={change}
                    required
                  />
                </label>

                <label className="cc-field">
                  <span>Last name <i>*</i></span>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Rivera"
                    value={form.lastName}
                    onChange={change}
                    required
                  />
                </label>
              </div>

              <div className="cc-book-row">
                <label className="cc-field">
                  <span>Work email <i>*</i></span>
                  <input
                    type="email"
                    name="email"
                    placeholder="name@school.edu"
                    value={form.email}
                    onChange={change}
                    required
                  />
                </label>

                <label className="cc-field">
                  <span>Phone number</span>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+1 555 655 5656"
                    value={form.phone}
                    onChange={change}
                  />
                </label>
              </div>

              <div className="cc-book-row">
                <label className="cc-field">
                  <span>Institution <i>*</i></span>
                  <input
                    type="text"
                    name="school"
                    placeholder="Northfield College"
                    value={form.school}
                    onChange={change}
                    required
                  />
                </label>

                <label className="cc-field">
                  <span>Student body <i>*</i></span>
                  <select name="role" value={form.role} onChange={change} required>
                    <option value="">Select…</option>
                    {SIZES.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="cc-field">
                <span>What would you like to cover?</span>
                <textarea
                  name="notes"
                  rows={4}
                  placeholder="Tell us about your office: what you're using now, and what isn't working."
                  value={form.notes}
                  onChange={change}
                />
              </label>

              <button type="submit" className="cc-book-submit" disabled={submitting}>
                {submitting ? "One moment…" : "Choose a time to meet"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default CcBook;
